import { useEffect, useRef, useState, useCallback } from "react";
import {
  NotificationsOutlined,
  DoneAll,
  Refresh,
  EmojiEventsOutlined,
  CreditCardOutlined,
  SettingsOutlined,
  CampaignOutlined,
  MarkEmailReadOutlined,
  DraftsOutlined,
} from "@mui/icons-material";
import {
  getMyNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
} from "../services/notification";
import { formatTimeAgo } from "../Utils/formatDateTime";

const getTypeConfig = (type) => {
  switch (type) {
    case "prediction":
      return {
        Icon: EmojiEventsOutlined,
        color: "var(--color-primary)",
        bgColor: "rgba(200, 255, 0, 0.12)",
        borderColor: "rgba(200, 255, 0, 0.25)",
        label: "Race Alert",
      };
    case "subscription":
      return {
        Icon: CreditCardOutlined,
        color: "#00A3FF",
        bgColor: "rgba(0, 163, 255, 0.12)",
        borderColor: "rgba(0, 163, 255, 0.25)",
        label: "Subscription",
      };
    case "system":
      return {
        Icon: SettingsOutlined,
        color: "var(--color-accent-amber)",
        bgColor: "rgba(255, 168, 0, 0.12)",
        borderColor: "rgba(255, 168, 0, 0.25)",
        label: "System",
      };
    case "promo":
    default:
      return {
        Icon: CampaignOutlined,
        color: "#9B59B6",
        bgColor: "rgba(155, 89, 182, 0.12)",
        borderColor: "rgba(155, 89, 182, 0.25)",
        label: "Promotion",
      };
  }
};

export default function NotificationPopover({ isAuthenticated = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // Ignore count fetch errors
    }
  }, [isAuthenticated]);

  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (!isAuthenticated) return;
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const data = await getMyNotifications(pageNum, 15);
      const results = data.results || (Array.isArray(data) ? data : []);
      const count = data.count || results.length;
      setTotalCount(count);

      if (append) {
        setNotifications((prev) => [...prev, ...results]);
      } else {
        setNotifications(results);
      }

      setHasMore(Boolean(data.next) || (results.length < count && pageNum * 15 < count));
      setPage(pageNum);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnreadCount();

    const handleNotificationReceived = () => {
      fetchUnreadCount();
      if (isOpen) {
        fetchNotifications(1);
      }
    };

    const handleServiceWorkerMessage = (event) => {
      if (
        event.data &&
        (event.data.type === "PLAY_NOTIFICATION_SOUND" ||
          event.data.type === "NOTIFICATION_RECEIVED")
      ) {
        handleNotificationReceived();
      }
    };

    window.addEventListener("notification-received", handleNotificationReceived);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // 30 seconds polling fallback

    return () => {
      window.removeEventListener("notification-received", handleNotificationReceived);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
      clearInterval(interval);
    };
  }, [isAuthenticated, fetchUnreadCount, isOpen, fetchNotifications]);

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications(1);
      fetchUnreadCount();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = async (e, notif) => {
    e.stopPropagation();
    if (notif.is_read) return;

    try {
      await markNotificationRead(notif.id);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notif.id ? { ...item, is_read: true, read_at: new Date().toISOString() } : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleToggleReadStatus = async (e, notif) => {
    e.stopPropagation();
    try {
      if (notif.is_read) {
        await markNotificationUnread(notif.id);
        setNotifications((prev) =>
          prev.map((item) => (item.id === notif.id ? { ...item, is_read: false } : item))
        );
        setUnreadCount((prev) => prev + 1);
      } else {
        await markNotificationRead(notif.id);
        setNotifications((prev) =>
          prev.map((item) => (item.id === notif.id ? { ...item, is_read: true } : item))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Failed to toggle read status:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  // Infinite scroll: auto-load more when sentinel becomes visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchNotifications(page + 1, true);
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, hasMore, loadingMore, loading, page, fetchNotifications]);

  const filteredNotifications = notifications.filter((item) => {
    if (filter === "unread") return !item.is_read;
    return true;
  });

  if (!isAuthenticated) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Notifications"
        aria-expanded={isOpen}
        className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-full text-[var(--color-text)] w-[38px] h-[38px] cursor-pointer inline-flex items-center justify-center"
      >
        <NotificationsOutlined sx={{ fontSize: 24 }} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-[var(--color-primary)] text-black font-extrabold text-[12px] h-5 min-w-5 rounded-full flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="md:absolute fixed md:right-0 right-4 md:top-[calc(100%+12px)] top-[68px] md:w-[380px] w-[calc(100vw-32px)] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] z-[9999] overflow-hidden flex flex-col">
          <div className="p-4 border-b border-[var(--color-border)] flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-[var(--color-text)] uppercase tracking-wider">
                Notifications
              </span>
              {unreadCount > 0 && (
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)] border border-[rgba(200,255,0,0.25)]">
                  {unreadCount} New
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="bg-transparent text-[var(--color-primary)] text-xs font-semibold flex items-center gap-1 cursor-pointer hover:underline"
                  title="Mark all as read"
                >
                  <DoneAll sx={{ fontSize: 16 }} />
                  <span>Mark all read</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => fetchNotifications(1)}
                className="bg-transparent text-[var(--color-text-muted)] cursor-pointer"
                title="Refresh"
              >
                <Refresh sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          <div className="p-4 border-b border-[var(--color-border)] flex items-center gap-2 bg-[var(--color-bg)]">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer transition-all ${
                filter === "all"
                  ? "bg-[var(--color-primary)] text-black"
                  : "bg-transparent text-[var(--color-text)]"
              }`}
            >
              All ({totalCount})
            </button>
            <button
              type="button"
              onClick={() => setFilter("unread")}
              className={`text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer transition-all ${
                filter === "unread"
                  ? "bg-[var(--color-primary)] text-black"
                  : "bg-transparent text-[var(--color-text)]"
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>

          <div className="md:max-h-[420px] max-h-[360px] overflow-y-auto no-scrollbar">
            {loading ? (
              <div className="grid gap-px">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />
                ))}
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="min-h-[180px] text-center flex flex-col items-center justify-center gap-3">
                <NotificationsOutlined
                  sx={{ fontSize: 40, color: "var(--color-text-muted)" }}
                />
                <p className="xl:text-lg md:text-base text-sm font-semibold text-[var(--color-text-muted)]">
                  {filter === "unread" ? "No unread notifications" : "No notifications yet"}
                </p>
              </div>
            ) : (
              <div>
                {filteredNotifications.map((notif) => {
                  const typeConfig = getTypeConfig(notif.type);
                  const TypeIcon = typeConfig.Icon;
                  const timeAgo = formatTimeAgo(notif.sent_at || notif.notification_created_at);

                  return (
                    <div
                      key={notif.id}
                      onClick={(e) => handleMarkAsRead(e, notif)}
                      className={`p-4 cursor-pointer flex items-start gap-4 group relative first:border-t-0 border-t border-[var(--color-border)] ${
                        !notif.is_read && "bg-[rgba(200,255,0,0.12)]"
                      }`}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: typeConfig.bgColor,
                          color: typeConfig.color,
                        }}
                      >
                        <TypeIcon sx={{ fontSize: 20 }} />
                      </div>

                      <div className="flex flex-col flex-1 min-w-0 gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="md:text-sm text-xs text-[var(--color-text)] font-extrabold">
                            {notif.title}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => handleToggleReadStatus(e, notif)}
                            className="xl:opacity-0 xl:group-hover:opacity-100 text-[var(--color-text)] transition-opacity cursor-pointer"
                            title={notif.is_read ? "Mark as unread" : "Mark as read"}
                          >
                            {notif.is_read ? (
                              <DraftsOutlined sx={{ fontSize: 16 }} />
                            ) : (
                              <MarkEmailReadOutlined sx={{ fontSize: 16 }} />
                            )}
                          </button>
                        </div>

                        <p className="md:text-sm text-xs text-[var(--color-text-muted)]">
                          {notif.body}
                        </p>

                        <div className="flex items-center justify-between md:text-sm text-xs text-[var(--color-text-muted)]">
                          <span>{timeAgo}</span>
                          <span
                            className="text-xs uppercase font-semibold px-1.5 py-0.5 border rounded-[var(--radius-md)]"
                            style={{
                              background: typeConfig.bgColor,
                              borderColor: typeConfig.borderColor,
                              color: typeConfig.color,
                            }}
                          >
                            {typeConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Sentinel for infinite scroll + loading indicator */}
                {hasMore && (
                  <div ref={sentinelRef} className="p-4 flex items-center justify-center gap-2 border-t border-[var(--color-border)]">
                    {loadingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                        <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Loading more...</span>
                      </>
                    ) : (
                      <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Scroll for more</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
